/*
  A/B test (Split Test Pro): grouped carousel rows on /collections/all.
  See sections/ab-collection-carousels.liquid.

  activate() moves the section's group shells next to the filter sidebar and fills
  each carousel with the product cards already rendered in #ProductGridContainer.
  When a filter changes, facets.js replaces the contents of #ProductGridContainer
  with the filtered cards and the rows are rebuilt from those.

  Rows with more cards than fit on screen loop endlessly and the arrows move one card at
  a time. Rows that fit stay still and hide their arrows.

  Split Test Pro variant JS:
    (function activate(tries) {
      if (window.ABCollectionCarousels) return window.ABCollectionCarousels.activate();
      if (tries < 50) setTimeout(function () { activate(tries + 1); }, 100);
    })(0);
*/
(() => {
  if (window.ABCollectionCarousels) return;

  const ACTIVE_CLASS = 'ab-carousels-active';
  const EMPTY_CLASS = 'ab-carousels-empty';

  let shell = null;
  let grid = null;
  let observer = null;
  let resizeTimer = null;
  let builtPerView = 0;
  // group element -> { slides, track, loops, step, start, setWidth, index }
  const rows = new Map();

  function activate() {
    if (shell && shell.isConnected) return true;

    const root = document.querySelector('[data-ab-collection-carousels]');
    grid = document.getElementById('ProductGridContainer');
    if (!root || !grid) return false;

    shell = root;
    // Take the grid's place in the vertical-facets layout. facets.js only replaces
    // the inner HTML of #ProductGridContainer, so the shell survives filter changes.
    grid.after(shell);
    shell.hidden = false;
    document.documentElement.classList.add(ACTIVE_CLASS);

    distribute();

    observer = new MutationObserver(onGridMutation);
    observer.observe(grid, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    shell.addEventListener('click', onArrowClick);
    window.addEventListener('resize', onResize);

    return true;
  }

  function onGridMutation(records) {
    // facets.js swaps in a new result set by replacing the container's children.
    const swapped = records.some((record) => record.type === 'childList' && record.target === grid && record.addedNodes.length);
    if (swapped) distribute();

    // facets.js adds .loading to #ProductGridContainer .collection while it fetches.
    shell.classList.toggle('is-loading', Boolean(grid.querySelector('.collection.loading')));
  }

  function distribute() {
    const cards = new Map();
    grid.querySelectorAll('li[data-product-card-id]').forEach((card) => {
      if (!hiddenByApp(card)) cards.set(card.dataset.productCardId, card);
    });

    const placed = new Set();
    let visibleGroups = 0;

    shell.querySelectorAll('[data-ab-group]').forEach((group) => {
      const ids = (group.dataset.productIds || '').split(',').filter(Boolean);
      const slides = [];

      ids.forEach((id) => {
        const card = cards.get(id);
        if (!card) return;

        // A product in several groups: move the original into the first group and
        // clone it into the rest.
        const slide = placed.has(id) ? prepareClone(card.cloneNode(true)) : card;
        placed.add(id);

        slide.classList.add('ab-collection-carousels__slide');
        // Cards start hidden for the reveal-on-scroll animation; facets.js cancels it the same way.
        slide.classList.add('scroll-trigger--cancel');
        slide.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'));
        slides.push(slide);
      });

      rows.set(group, { slides });
      group.hidden = slides.length === 0;
      if (slides.length) visibleGroups += 1;
    });

    // No results, or no result is in any group: show the original grid instead.
    document.documentElement.classList.toggle(EMPTY_CLASS, visibleGroups === 0);

    const unplaced = [...cards.keys()].filter((id) => !placed.has(id));
    if (unplaced.length) {
      console.debug('[ab-collection-carousels] products not in any group:', unplaced.map((id) => cards.get(id).dataset.productCardHandle));
    }

    buildRows();
  }

  function cardsPerView() {
    return parseInt(getComputedStyle(shell).getPropertyValue('--ab-cards-per-view'), 10) || 1;
  }

  // (Re)build every row. Each build swaps in a fresh track, so scroll listeners and loop
  // copies from the previous build go away with the old element.
  function buildRows() {
    builtPerView = cardsPerView();
    rows.forEach((row, group) => {
      const oldTrack = group.querySelector('.ab-collection-carousels__track');
      const track = oldTrack.cloneNode(false);
      row.track = track;
      row.loops = !group.hidden && row.slides.length > builtPerView;

      if (row.loops) {
        // Copies of the last cards go before the real ones and copies of the first cards
        // after, enough to fill the screen plus one. The arrows and swipes move one card
        // at a time, so the row can never run past the copies before it re-centres.
        const copies = Math.min(row.slides.length, builtPerView + 1);
        const before = row.slides.slice(-copies).map(loopCopy);
        const after = row.slides.slice(0, copies).map(loopCopy);
        track.append(...before, ...row.slides, ...after);
        watchScroll(row);
        watchWheel(row);
      } else {
        track.append(...row.slides);
      }

      oldTrack.replaceWith(track);
      group.querySelector('.ab-collection-carousels__slider').toggleAttribute('data-loop', row.loops);
    });
    placeRows(() => 0);
  }

  // Measure every row first, then set every scroll position, so the browser lays the
  // page out once rather than once per row.
  function placeRows(indexFor) {
    const looping = [...rows.values()].filter((row) => row.loops);
    looping.forEach((row) => {
      const [first, second] = row.slides;
      const padding = parseFloat(getComputedStyle(row.track).paddingLeft) || 0;
      row.index = indexFor(row);
      row.step = second.offsetLeft - first.offsetLeft;
      row.start = first.offsetLeft - padding;
      row.setWidth = row.step * row.slides.length;
    });
    looping.forEach((row) => jumpTo(row, row.start + row.index * row.step));
  }

  function currentIndex(row) {
    if (!row.step) return 0;
    const raw = Math.round((row.track.scrollLeft - row.start) / row.step);
    const count = row.slides.length;
    return ((raw % count) + count) % count;
  }

  // Instant scroll, used for the invisible jump from a copy back to the real card.
  function jumpTo(row, left) {
    row.track.style.scrollBehavior = 'auto';
    row.track.scrollLeft = left;
    row.track.style.scrollBehavior = '';
  }

  // When the row comes to rest on a copy, jump by one full set to the identical real card.
  function recenter(row) {
    if (!row.loops || !row.step || !row.track.isConnected) return;
    const left = row.track.scrollLeft;
    if (left < row.start - row.step / 2) {
      jumpTo(row, left + row.setWidth);
    } else if (left > row.start + row.setWidth - row.step / 2) {
      jumpTo(row, left - row.setWidth);
    }
  }

  function watchScroll(row) {
    let idleTimer;
    // scrollend isn't supported everywhere (older Safari), so also re-centre once
    // scrolling has been quiet for a moment.
    row.track.addEventListener(
      'scroll',
      () => {
        clearTimeout(idleTimer);
        idleTimer = setTimeout(() => recenter(row), 150);
      },
      { passive: true }
    );
    row.track.addEventListener('scrollend', () => recenter(row));
  }

  // Trackpad: Chrome's scroll snapping pulls the small steps of a two-finger swipe back
  // to the current card, so the row never moves. Take sideways wheel input over and move
  // one card per swipe, like the arrows. Vertical scrolling is left to the page.
  function watchWheel(row) {
    let swiping = false; // inside a swipe (including its momentum)
    let sideways = 0; // this swipe's total sideways movement
    let upDown = 0; // and its total up-and-down movement
    let travel = 0;
    let moved = false;
    let movedAt = 0;
    let lastSpeed = 0;
    let lastX = 0;
    let quietTimer;

    function startSwipe() {
      swiping = true;
      sideways = 0;
      upDown = 0;
      travel = 0;
      moved = false;
    }

    row.track.addEventListener(
      'wheel',
      (event) => {
        const scale = event.deltaMode === WheelEvent.DOM_DELTA_LINE ? 16 : 1;
        const dx = event.deltaX * scale;
        const dy = event.deltaY * scale;
        const now = performance.now();

        // After a swipe, a Mac trackpad keeps sending momentum events for a few seconds.
        // Momentum only slows down and keeps its direction, so a clear speed-up or a change
        // of direction means the fingers are swiping again: start a new swipe. (The short
        // wait lets the previous card finish sliding in.) Once a sideways swipe has moved
        // the row, only its sideways speed counts, so up-and-down wobble isn't mistaken
        // for a new swipe.
        const speed = moved ? Math.abs(dx) : Math.abs(dx) + Math.abs(dy);
        const sped = speed >= 4 && speed > lastSpeed * 1.5;
        const reversed = Math.abs(dx) >= 4 && lastX && Math.sign(dx) !== Math.sign(lastX);
        if (!swiping || ((sped || reversed) && now - movedAt > 200)) startSwipe();
        lastSpeed = speed;
        lastX = dx;

        // Otherwise the swipe is over once the wheel events stop.
        clearTimeout(quietTimer);
        quietTimer = setTimeout(() => {
          swiping = false;
        }, 150);

        // Judge the direction from the whole swipe, not each event, so a slightly wobbly
        // sideways swipe still counts. Mostly up-and-down swipes scroll the page.
        sideways += Math.abs(dx);
        upDown += Math.abs(dy);
        if (!moved && sideways <= upDown) return;
        event.preventDefault();
        if (moved) return;

        travel += dx;
        if (Math.abs(travel) < 20) return;
        moved = true;
        movedAt = now;
        moveBy(row, Math.sign(travel));
      },
      { passive: false }
    );
  }

  function onArrowClick(event) {
    const arrow = event.target.closest('.ab-collection-carousels__arrow');
    if (!arrow) return;
    const row = rows.get(arrow.closest('[data-ab-group]'));
    if (!row || !row.loops) return;
    moveBy(row, arrow.dataset.direction === 'prev' ? -1 : 1);
  }

  // Move one card forward (1) or back (-1).
  function moveBy(row, direction) {
    recenter(row);
    row.track.scrollBy({ left: direction * row.step, behavior: 'smooth' });
  }

  // Loop copies are on screen only while the row is moving, so they drop parts nobody
  // sees: the hidden multi-image slider under the featured image, the hidden
  // strike-through price and inline scripts. They're also hidden from screen readers
  // and keyboard focus.
  function loopCopy(slide) {
    const copy = prepareClone(slide.cloneNode(true));
    copy.classList.add('is-loop-clone');
    copy.setAttribute('aria-hidden', 'true');
    copy.removeAttribute('id');
    copy.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    copy.querySelectorAll('aa-card-carousel[data-product-url]').forEach((slider) => slider.parentElement.remove());
    copy.querySelectorAll('.strike-through-pricing, script').forEach((element) => element.remove());
    copy
      .querySelectorAll('a, button, input, select, textarea, summary, [tabindex]')
      .forEach((element) => element.setAttribute('tabindex', '-1'));
    return copy;
  }

  // The gift card app hides its personalized gift card product from product grids with
  //   #product-grid .grid__item:has([href*="/personalized-gift-card"]) { display: none }
  // The rows sit outside #product-grid, so leave those cards out to match the normal grid.
  function hiddenByApp(card) {
    return Boolean(card.querySelector('[href*="/personalized-gift-card"]'));
  }

  // Event listeners aren't cloned, so Klaviyo's Notify Me button would do nothing in a
  // copy. Use the same plain link facets.js puts in re-rendered cards.
  function prepareClone(clone) {
    const container = clone.querySelector('.klaviyo-button-container');
    const link = clone.querySelector('.full-unstyled-link');
    if (container && link) {
      const notifyMe = document.createElement('a');
      notifyMe.className = 'button button--full-width button--secondary quick-add__submit klaviyo-bis-trigger-styling';
      notifyMe.href = link.getAttribute('href');
      notifyMe.textContent = 'Notify Me';
      container.replaceChildren(notifyMe);
    }
    return clone;
  }

  // Crossing a breakpoint changes how many cards fit, which decides whether a row loops
  // and how many copies it needs, so rebuild. Otherwise keep each row on its card.
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      if (cardsPerView() !== builtPerView) {
        buildRows();
      } else {
        placeRows(currentIndex);
      }
    }, 150);
  }

  function shouldAutoActivate() {
    if (new URLSearchParams(window.location.search).get('ab_carousels') === '1') return true;
    return Boolean(document.querySelector('[data-ab-collection-carousels][data-editor-preview]'));
  }

  window.ABCollectionCarousels = { activate };

  if (shouldAutoActivate()) activate();

  // Theme editor: re-rendering either section replaces the DOM this script moved
  // cards around in, so reload the preview to start clean. Turning on the preview
  // setting re-renders the section without re-running this script, so activate here.
  document.addEventListener('shopify:section:load', () => {
    if (!window.Shopify || !window.Shopify.designMode) return;
    if (shell) {
      window.location.reload();
    } else if (shouldAutoActivate()) {
      activate();
    }
  });
})();
